/**
 * FilterSidebar — reusable sidebar+content layout for filter-heavy pages.
 * Uses children for sidebar filter controls and a 'content' prop for the main
 * content area. Desktop: CSS Grid 2-column with sticky sidebar. Mobile: Radix
 * Dialog left-edge drawer.
 */
import { useState } from 'react';
import { LuSlidersHorizontal, LuX } from 'react-icons/lu';
import { cn } from '../../client/cn';
import { DrawerDialog, DrawerDialogClose } from '../ui';

export interface FilterSidebarProps {
  /** Filter controls rendered in the sidebar. */
  children: React.ReactNode;
  /** Main content area. */
  content: React.ReactNode;
  /** Heading label for the sidebar. */
  sidebarLabel: string;
  /** When provided, a Clear All button is rendered; callback fires on click. */
  onReset?: () => void;
}

export interface FilterSectionProps {
  heading: string;
  children: React.ReactNode;
}

/**
 * Labeled grouping for filter controls within FilterSidebar. Renders a heading
 * (h3) followed by children filter primitives.
 */
export function FilterSection({ heading, children }: FilterSectionProps) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-widest text-neutral-500 dark:text-neutral-500 mb-3">
        {heading}
      </h3>
      {children}
    </div>
  );
}

function SidebarContents({
  sidebarLabel,
  onReset,
  children,
  closeButton,
}: {
  sidebarLabel: string;
  onReset?: () => void;
  children: React.ReactNode;
  closeButton?: React.ReactNode;
}) {
  return (
    <>
      <div className="flex items-center justify-between pb-3 mb-5 border-b border-neutral-200/60 dark:border-strokedark/60">
        <span className="text-xs font-bold uppercase tracking-widest text-neutral-900 dark:text-white">
          {sidebarLabel}
        </span>
        <div className="flex items-center gap-2">
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              className="text-[11px] font-medium text-primary-700 hover:text-primary-800 transition-colors dark:text-primary-400 dark:hover:text-primary-300"
            >
              Clear All
            </button>
          )}
          {closeButton}
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </>
  );
}

/**
 * Reusable sidebar+content layout shell. Accepts children (filter controls) in
 * the sidebar and a 'content' prop (ReactNode) for the main content area.
 * Desktop: CSS Grid 2-column with sticky sidebar. Mobile: Radix Dialog
 * left-edge drawer.
 */
export function FilterSidebar({
  children,
  content,
  sidebarLabel,
  onReset,
}: FilterSidebarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    // FilterSidebar:full-bleed — escapes parent max-w-7xl to use full viewport
    // width so the sidebar sits further left and content area fits 3+ columns on
    // wide screens. The negative-margin + padding technique keeps inner content
    // horizontally aligned while the grid spans edge-to-edge.
    <div
      className={cn(
        'relative w-[100vw] left-1/2 -ml-[50vw]',
        'px-4 sm:px-6 lg:px-12 xl:px-16 2xl:px-24',
      )}
    >
      {/* Mobile filter toggle — hidden at lg+ */}
      <div className="flex items-center justify-center mb-4 lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open filters"
          className={cn(
            'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
            'bg-white dark:bg-boxdark border border-neutral-200 dark:border-strokedark',
            'text-neutral-700 dark:text-neutral-200',
            'hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors',
          )}
        >
          <LuSlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          Filters
        </button>
      </div>

      {/* Desktop 2-column grid — sidebar visible at lg+ */}
      <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-10">
        {/* Desktop sidebar */}
        <aside
          className={cn(
            'hidden lg:block',
            'sticky top-[5.5rem] self-start max-h-[calc(100vh-6.5rem)] overflow-y-auto',
            'bg-white/50 dark:bg-boxdark/80 rounded-xl',
            'border border-neutral-200/50 dark:border-primary-900/20',
            'p-5',
          )}
        >
          <SidebarContents sidebarLabel={sidebarLabel} onReset={onReset}>
            {children}
          </SidebarContents>
        </aside>

        {/* Main content area */}
        <div>{content}</div>
      </div>

      <DrawerDialog
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={sidebarLabel}
        contentClassName="no-scrollbar"
      >
        <SidebarContents
          sidebarLabel={sidebarLabel}
          onReset={onReset}
          closeButton={
            <DrawerDialogClose
              ariaLabel="Close filters"
              className={cn(
                'inline-flex items-center justify-center rounded-md p-1',
                'text-neutral-500 dark:text-neutral-300',
                'hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-white',
                'transition-colors',
              )}
            >
              <>
                <LuX className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Close filters</span>
              </>
            </DrawerDialogClose>
          }
        >
          {children}
        </SidebarContents>
      </DrawerDialog>
    </div>
  );
}
